import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Maximize2, Minimize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerSection,
  DrawerDetailRow,
  DrawerFooter,
} from '@/components/ui/drawer'
import { cn } from '@/utils/cn'
import { getReceptionHistoryById } from '@/api/history.api'
import type { ReceptionHistory, ChatMessage } from '@/types/api'
import { formatDateTime } from '@/utils/format'

interface HistoryDetailDrawerProps {
  item: ReceptionHistory | null
  onClose: () => void
  showUnitColumn?: boolean
}

export function HistoryDetailDrawer({
  item,
  onClose,
  showUnitColumn,
}: HistoryDetailDrawerProps) {
  const [isFullScreen, setIsFullScreen] = useState(false)

  const { data: detail, isLoading } = useQuery({
    queryKey: ['reception-history', 'detail', item?.id],
    queryFn: ({ signal }) => getReceptionHistoryById(item!.id, signal),
    enabled: !!item,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const data = detail ?? item
  const messages = detail?.chatMessages || []

  const handleClose = () => {
    setIsFullScreen(false)
    onClose()
  }

  if (!item) return null

  return (
    <Drawer
      open={!!item}
      onClose={handleClose}
      title={
        isFullScreen
          ? 'XEM CHI TIẾT THÔNG TIN HỘI THOẠI'
          : 'XEM CHI TIẾT THÔNG TIN TIẾP ĐÓN'
      }
      size={isFullScreen ? '100vw' : 800}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        content: {
          transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
        },
      }}
      footer={
        <DrawerFooter>
          <Button
            variant="outline"
            size="md"
            onClick={handleClose}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
        </DrawerFooter>
      }
    >
      <div className="flex flex-col flex-1 h-full min-h-0 overflow-hidden">
        <div
          className={cn(
            'flex flex-col flex-1 min-h-0 overflow-y-auto',
            isFullScreen ? 'p-4 bg-gray-50/50' : '',
          )}
        >
          <div
            className={cn(
              'flex flex-col flex-1 min-h-0',
              isFullScreen ? 'w-full h-full bg-white' : '',
            )}
          >
            {!isFullScreen && (
              <DrawerSection className="shrink-0">
                <DrawerDetailRow
                  label="Tên hội thoại:"
                  value={data?.title || '--'}
                  valueClassName="text-left break-words max-w-[400px]"
                />
                <DrawerDetailRow
                  label="Tên khách hàng:"
                  value={data?.customerName || '--'}
                  valueClassName="text-left break-words max-w-[400px]"
                />
                <DrawerDetailRow
                  label="Ngày thực hiện:"
                  value={data?.startTime ? formatDateTime(data.startTime) : '—'}
                  valueClassName="text-left break-words max-w-[400px]"
                />
                <DrawerDetailRow
                  label="Robot thực hiện:"
                  value={data?.robotName || '—'}
                  valueClassName="text-left break-words max-w-[400px]"
                />
                {showUnitColumn && (
                  <DrawerDetailRow
                    label="Đơn vị:"
                    value={data?.unitName || '—'}
                    valueClassName="text-left break-words max-w-[400px]"
                  />
                )}
                <DrawerDetailRow
                  label="Nội dung tóm tắt:"
                  value={data?.summary || '—'}
                  valueClassName="text-left break-words max-w-[400px]"
                  alignTop
                />
              </DrawerSection>
            )}

            <div className="relative flex flex-col flex-1 min-h-187 w-full">
              <DrawerSection
                title={isFullScreen ? '' : 'Thông tin hội thoại'}
                className="relative flex flex-col flex-1 min-h-0 pt-2"
                bodyClassName="flex-1 overflow-y-auto py-3"
              >
                <div className="absolute top-5 right-5 z-10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsFullScreen(!isFullScreen)}
                    className="gap-2 text-[#CE7A58] border-[#CE7A58] bg-white hover:bg-[#CE7A581A] h-7 text-xs px-2.5 shadow-sm"
                  >
                    {isFullScreen ? (
                      <Minimize2 className="w-3.5 h-3.5" />
                    ) : (
                      <Maximize2 className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </div>

                <div className="space-y-4">
                  {isLoading ? (
                    <div className="py-6 text-center text-sm text-gray-400">
                      Đang tải...
                    </div>
                  ) : messages.length > 0 ? (
                    messages.map((msg, idx) => (
                      <MessageBubble key={idx} message={msg} />
                    ))
                  ) : (
                    <div className="py-6 text-center text-sm text-gray-400">
                      Chưa có nội dung hội thoại.
                    </div>
                  )}
                </div>
              </DrawerSection>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.type.toLowerCase() === 'user'

  return (
    <div
      className={cn(
        'flex flex-col w-full',
        isUser ? 'items-end' : 'items-start',
      )}
    >
      {!isUser && (
        <span className="mb-2 ml-1 text-[13px] font-medium text-[#CE7A58]">
          {message.type.toLowerCase() === 'robot'
            ? 'Robot'
            : message.type || 'Robot'}
        </span>
      )}

      <div
        className={cn(
          'flex flex-col rounded-2xl px-5 py-4 text-[14px] leading-relaxed text-[#333333]',
          isUser
            ? 'max-w-[80%] bg-[#CE7A581A]'
            : 'max-w-[80%] bg-white border border-solid border-[#CE7A58]',
        )}
      >
        <div className="whitespace-pre-wrap">{message.context}</div>

        <span className="mt-2 text-[12px] text-[#1E2F4159]">
          {message.timestamp
            ? formatDateTime(new Date(message.timestamp), 'HH:mm')
            : '--:--'}
        </span>
      </div>
    </div>
  )
}
